<template>
  <Layout>
    <div class="mc-list-header">
      <PageHeader :title="title">
        <template #afterTitle>
          <div class="d-flex align-items-center gap-2">
            <McButtonBack href="/report" :query="{ domain: domain }" />
            <ShareReport
              :report="report"
              :filter="baseFilter"
              :dropdownItems="itemDownloadDropdown"
              @doShare="downloadReport"
            />
            <DownloadReportDropdown
              :report="report"
              :items="itemDownloadDropdown"
              :actualTotal="actualTotal"
            />
          </div>
        </template>
      </PageHeader>
    </div>
    <div class="d-flex align-items-center gap-2 me-2 absolute-right">
      <FavoriteReportButton
        v-if="hasAccess(['favorite-reports:update']) && !report.isSaved"
        :report="report"
      />
      <ModalSavedReport
        v-if="hasAccess(['saved-reports:read']) && !report.isSaved"
        :filter="filterValue"
        :detail="filterDetail"
        :report="report"
      />
      <McButton
        variant="secondary"
        :text="$t('report.title.reportParameter')"
        icon="icon-outline_filter"
        @click="showModal"
      />
    </div>
    <b-card :key="summaryKey" class="mb-2 mc-general__label" style="margin-top: 80px">
      <h5 class="mc-general__label">{{ $t('report.fields.dataSummary') }}</h5>
      <b-row>
        <b-col class="pt-2" sm="14" md="2">
          <b-form-group
            id="periodReport_label"
            :label="$t('report.object.reportPeriod')"
            label-for="reportPeriod"
          >
            <span v-if="!isBusy">
              {{ periodeLabel }}
            </span>
            <b-skeleton v-else width="70%" />
          </b-form-group>
        </b-col>
        <b-col class="pt-2" sm="14" md="2">
          <b-form-group
            id="licensePlate_label"
            :label="$t('report.fields.licensePlate')"
            label-for="licensePlate"
          >
            <div v-if="!isBusy" class="d-flex align-items-center gap-2">
              <span>{{ licensePlateLabel }}</span>
              <McBadge
                v-if="vehicleIdCount > 2"
                :text="`+${vehicleIdCount - 2}`"
                variant="success"
                size="small"
                type="pill"
              />
            </div>
            <b-skeleton v-else width="70%" />
          </b-form-group>
        </b-col>
        <b-col class="pt-2" sm="14" md="2">
          <b-form-group
            id="totalTrip_label"
            :label="$t('report.fields.totalVehicleTrip')"
            label-for="totalTrip"
          >
            <span v-if="!isBusy">
              {{ totalRows }}
            </span>
            <b-skeleton v-else width="70%" />
          </b-form-group>
        </b-col>
        <b-col v-for="item of summaryFields" :key="`key-${item.key}`" class="pt-2" sm="14" md="2">
          <b-form-group
            :id="`${item.key}_label`"
            :label="`${item.label}`"
            :label-for="`${item.key}`"
          >
            <span v-if="!isBusy">
              {{ summary[item.key] ? item.formatter(summary[item.key]) : '-' }}
            </span>
            <b-skeleton v-else width="70%" />
          </b-form-group>
        </b-col>
        <b-col v-if="hasAdditionalFilterScope" class="pt-2" sm="14" md="2">
          <b-form-group
            id="minKmMileage_label"
            :label="$t('report.text.minKmMileage')"
            label-for="minKmMileage"
          >
            <span v-if="!isBusy">
              {{ minKmMileageLabel }}
            </span>
            <b-skeleton v-else width="70%" />
          </b-form-group>
        </b-col>
        <b-col v-if="hasAdditionalFilterScope" class="pt-2" sm="14" md="2">
          <b-form-group
            id="minMinuteTripDuration_label"
            :label="$t('report.text.minMinuteTripDuration')"
            label-for="minMinuteTripDuration"
          >
            <span v-if="!isBusy">
              {{ minMinuteTripDurationLabel }}
            </span>
            <b-skeleton v-else width="70%" />
          </b-form-group>
        </b-col>
      </b-row>
    </b-card>
    <McAlert
      v-if="isHaveFuelUltrasonicSensor"
      variant="info"
      :html="$t('report.text.haveFuelSensor')"
      withIcon
      :withGap="false"
    />
    <AlertReportTooMany v-if="actualTotal > totalRows" />
    <McGrapqlTable
      v-if="Object.keys(filters).length > 0"
      :key="tableKey"
      bordered
      withCard
      searchable
      :fields="fields"
      :apiClient="apiClient"
      :hasCheckbox="false"
      :lazyLoadAddress="columns.includes('startAddress') || columns.includes('endAddress')"
      reportKey="vehicle-trip"
    >
      <template #viewManager="{ handleSetDataFields, dataColumns, initialFields }">
        <McFilterSelect
          :defaultColumnOrder="initialFields"
          :currentColumnOrder="dataColumns"
          @setColumn="handleSetDataFields"
          @setColumnReport="handleColumnReport"
        />
      </template>
      <template #cell(startTime)="data">
        {{
          data.item.reportVehicleTrip
            ? momentDateShortMonth(data.item.reportVehicleTrip.startTime)
            : '-'
        }}<br />
        <span class="text-muted">{{
          data.item.reportVehicleTrip
            ? momentTimeSeconds(data.item.reportVehicleTrip.startTime, true)
            : '-'
        }}</span>
      </template>
      <template #cell(stopTime)="data">
        {{
          data.item.reportVehicleTrip
            ? momentDateShortMonth(data.item.reportVehicleTrip.stopTime)
            : '-'
        }}<br />
        <span class="text-muted">{{
          data.item.reportVehicleTrip
            ? momentTimeSeconds(data.item.reportVehicleTrip.stopTime, true)
            : '-'
        }}</span>
      </template>
      <template #cell(driver)="data">
        {{
          data.item.reportVehicleTrip.vehicle
            ? data.item.reportVehicleTrip.driver
              ? data.item.reportVehicleTrip.driver.fullname
              : '-'
            : '-'
        }}
      </template>

      <template #cell(vehicle)="data">
        {{
          data.item.reportVehicleTrip.vehicle
            ? data.item.reportVehicleTrip.vehicle.licensePlate
            : '-'
        }}
      </template>

      <template #cell(startLocation)="data">
        <b-skeleton v-if="data.item.reportVehicleTrip.startAddress == ''" width="70%" />
        <AddressValue
          v-else
          :id="`start-address-${data.index}`"
          :latitude="data.item.reportVehicleTrip.startLat"
          :longitude="data.item.reportVehicleTrip.startLong"
          :address="data.item.reportVehicleTrip.startAddress"
        />
      </template>

      <template #cell(endLocation)="data">
        <b-skeleton v-if="data.item.reportVehicleTrip.stopAddress == ''" width="70%" />
        <AddressValue
          v-else
          :id="`stop-address-${data.index}`"
          :latitude="data.item.reportVehicleTrip.stopLat"
          :longitude="data.item.reportVehicleTrip.stopLong"
          :address="data.item.reportVehicleTrip.stopAddress"
        />
      </template>

      <template #cell(startGeofence)="data">
        <HuggedValue
          :id="`start-geofence-${data.index}`"
          :value="data.item.reportVehicleTrip.startGeofence"
        />
      </template>

      <template #cell(stopGeofence)="data">
        <HuggedValue
          :id="`stop-geofence-${data.index}`"
          :value="data.item.reportVehicleTrip.stopGeofence"
        />
      </template>

      <template #cell(vehicleType)="data">
        {{ transformVehicleType(data.item.reportVehicleTrip.vehicle) }}
      </template>

      <template #cell(tripMileage)="data">
        {{ minimizeFloat(data.item.reportVehicleTrip.tripMileage) }}
      </template>

      <template #cell(totalTimeTrip)="data">
        {{ data.item.totalTimeTrip }}
      </template>

      <template #cell(totalHourStop)="data">
        {{ data.item.totalHourStop }}
      </template>

      <template #cell(fuelEficiency)="data">
        <span v-if="ishaveOdoFuelSensor(data.item.reportVehicleTrip.sensors)">-</span>
        <span v-else>
          {{ minimizeFloat(data.item.vehicle.vehicleType.fuelEfficiency) }}
        </span>
      </template>

      <template #cell(tripFuel)="data">
        <div
          v-if="data.item.reportVehicleTrip.tripFuel != null"
          class="d-flex align-items-center gap-1"
        >
          <span>
            {{
              typeof data.item.reportVehicleTrip.tripFuel == 'number'
                ? minimizeFloat(data.item.reportVehicleTrip.tripFuel)
                : data.item.reportVehicleTrip.tripFuel
            }}
          </span>
          <i
            v-if="data.item.reportVehicleTrip.tripFuel == 'null (<10km)'"
            v-b-tooltip.hover
            class="mc-fa icon-outline_info"
            :title="$t('report.text.nullFuelTrip')"
          ></i>
        </div>
        <span v-else-if="ishaveOdoFuelSensor(data.item.reportVehicleTrip.sensors)">-</span>
        <EmptyValue v-else :id="`fuel-empty-${data.index}`" />
      </template>

      <template #cell(efficiency)="data">
        <span v-if="ishaveOdoFuelSensor(data.item.reportVehicleTrip.sensors)">-</span>
        <span v-else>
          {{ minimizeFloat(data.item.efficiency) }}
        </span>
      </template>

      <template #cell(standardFuel)="data">
        <span v-if="ishaveOdoFuelSensor(data.item.reportVehicleTrip.sensors)">-</span>
        <span v-else>
          {{ minimizeFloat(data.item.standardFuel) }}
        </span>
      </template>

      <template #cell(averageSpeed)="data">
        {{ minimizeFloat(data.item.averageSpeed) }}
      </template>

      <template #cell(lastOdometer)="data">
        {{ thousandFormat(data.item.reportVehicleTrip.lastOdometer) }}
      </template>

      <template #cell(sensorType)="data">
        {{ data.item.reportVehicleTrip.sensorType }}
      </template>
    </McGrapqlTable>
    <ModalFilter
      v-if="isMounted"
      ref="modal-filter"
      :report="report"
      :unRedirect="true"
      @refresh-table="forceRerender"
    />
    <McReportDownloader />
    <ModalSavedReportVerification
      :key="modalVerifReport"
      :domain="domain"
      :message="message"
      :error="isError"
    />
    <ModalEmail
      v-if="actualTotal > totalRows"
      :id="idEmailModal"
      :recipients="recipientsEmail"
      @submit-emails="handleEmails"
    />
  </Layout>
</template>
<script>
import appConfig from '@/app.config'
import McAlert from '@/components/McAlert'
import McBadge from '@/components/McBadge'
import McButton from '@/components/McButton'
import McButtonBack from '@/components/McButtonBack'
import McFilterSelect from '@/components/McFilterSelect'
import McGrapqlTable from '@/components/McGrapqlTable'
import McReportDownloader from '@/components/McReportDownloader'
import PageHeader from '@/components/page-header-v2'
import { Helper } from '@/mixin/helper'
import { MixPanel } from '@/mixin/mixpanel'
import { QueryCache } from '@/mixin/query-cache'
import responsive from '@/mixin/responsive'
import Layout from '@/router/layouts/main'
import AlertReportTooMany from '@/router/views/report/components/AlertReportTooMany'
import DownloadReportDropdown from '@/router/views/report/components/DownloadReportDropdown'
import FavoriteReportButton from '@/router/views/report/components/FavoriteReportButton'
import ModalEmail from '@/router/views/report/components/ModalEmail'
import ModalFilter from '@/router/views/report/components/ModalFilter'
import reportFilterFields from '@/router/views/report/components/ModalFilter/components/InputTableFilter/lib/reportFilterFields'
import ModalSavedReport from '@/router/views/report/components/ModalSavedReport'
import ModalSavedReportVerification from '@/router/views/report/components/ModalSavedReportVerification'
import { EXPORT_REPORT_VEHICLE_TRIP, GET_REPORT_HISTORY_FLEET } from '@/services/api/report/report'
import moment from 'moment'
import { mapGetters } from 'vuex'
import AddressValue from '../../components/AddressValue.vue'
import EmptyValue from '../../components/EmptyValue.vue'
import HuggedValue from '../../components/HuggedValue.vue'
import ShareReport from '../../components/ShareReport.vue'
import reportList from '../../lib/reportList.js'
import { SENSOR_CODE } from '../../lib/variables'
import { getFuelSensorString } from '../fuel/lib/helper/fuel-helper.js'
import fields from './lib/fields/fields'
moment.locale('id')

export default {
  page: {
    title: 'Report',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McButtonBack,
    McGrapqlTable,
    McFilterSelect,
    McReportDownloader,
    McButton,
    ModalFilter,
    McBadge,
    ModalSavedReport,
    ModalSavedReportVerification,
    FavoriteReportButton,
    DownloadReportDropdown,
    ModalEmail,
    AlertReportTooMany,
    ShareReport,
    McAlert,
    AddressValue,
    HuggedValue,
    EmptyValue,
  },
  mixins: [Helper, MixPanel, responsive, reportFilterFields],
  data() {
    return {
      isMounted: false,
      excelIcon: require('@/assets/images/download-format/excel.png'),
      pdfIcon: require('@/assets/images/download-format/pdf.png'),
      totalRows: 1,
      filters: {},
      tableKey: 0,
      summary: {},
      isBusy: false,
      reportFields: fields.map((item, i) => ({ ...item, order: i + 1 })),
      reportKey: 'history-vehicle',
      filterDetail: {},
      summaryKey: 50,
      queryCache: new QueryCache(),
      message: '',
      isError: false,
      modalVerifReport: 100,
      emails: [],
      recipientsEmail: [],
      isSubmitEmail: false,
      idEmailModal: 'modal-email-vehicle-trip',
      reportAfterEmail: '',
      actualTotal: 0,
      isHaveFuelUltrasonicSensor: false,
    }
  },
  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
      getFeatureFlagData: 'identity/getFeatureFlagData',
    }),
    domain() {
      return this.report.domain
    },
    title() {
      return this.report.labelName ?? this.$t(this.report.label)
    },
    fields() {
      return this.reportFields
    },
    columns() {
      const finalFields = []

      this.reportFields.forEach((field) => {
        if (Object.hasOwn(field, 'children')) {
          finalFields.push(...field.children)
        } else {
          finalFields.push(field)
        }
      })
      return finalFields
        .filter((item) => item.shown || item.shown === undefined)
        .map((field) => field.header)
    },
    summaryFields() {
      return [
        {
          key: 'totalTripMileage',
          label: this.$t('report.fields.totalTripMileage'),
          formatter: (value) => {
            return `${value} Km`
          },
        },
        {
          key: 'totalTimeTrip',
          label: this.$t('report.fields.totalTimeTrip'),
          formatter: (value) => {
            const duration = value.split(':')
            return `${duration[0]} ${this.$t('global.time.hours')}, ${duration[1]} ${this.$t(
              'global.time.minutes'
            )}, ${duration[2]} ${this.$t('global.time.seconds')}`
          },
        },
        {
          key: 'totalStandardFuel',
          label: this.$t('report.fields.totalStandardFuel'),
          formatter: (value) => {
            return `${value} Liter`
          },
        },
        {
          key: 'totalActualFuel',
          label: this.$t('report.fields.totalActualFuel'),
          formatter: (value) => {
            return `${value} Liter`
          },
        },
        {
          key: 'totalAverageSpeed',
          label: this.$t('report.fields.totalAverageSpeed'),
          formatter: (value) => {
            return `${value} ${this.$t('global.unit.kmPerHour')}`
          },
        },        
        { 
          key: 'totalHourStop',
          label: this.$t('report.fields.totalHourStop'),
          formatter: (value) => {
            const duration = value.split(':')
            return `${duration[0]} ${this.$t('global.time.hours')}, ${duration[1]} ${this.$t(
              'global.time.minutes'
            )}, ${duration[2]} ${this.$t('global.time.seconds')}`
          },
        },
        { 
          key: 'totalIdleTime',
          label: this.$t('report.fields.totalIdleTime'),
          formatter: (value) => {
            const duration = value.split(':')
            return `${duration[0]} ${this.$t('global.time.hours')}, ${duration[1]} ${this.$t(
              'global.time.minutes'
            )}, ${duration[2]} ${this.$t('global.time.seconds')}`
          },
        },
        { 
          key: 'averageFuelEfficiency',
          label: this.$t('report.fields.averageFuelEfficiency'),
          formatter: (value) => {
            return `${value} ${this.$t('global.unit.kmPerLiter')}`
          }
        },
      ]
    },
    baseFilter() {
      const filter = this.getBaseFilter(this.filters)
      const result = {
        ...filter,
        column: this.columns,
      }
      if (
        this.getFeatureFlagData('vsms2_report_vehicle_trip_version') &&
        this.checkApiVersion(result, this.report.reportKey ?? this.report.key)
      ) {
        result.version = this.getFeatureFlagData('vsms2_report_vehicle_trip_version').value
      }
      return result
    },
    generateFilter() {
      const { valid, msg } = this.validateFilter(this.report.filter, this.filters)
      if (valid) {
        const model = this.baseFilter
        return { valid: true, model }
      } else {
        this.$mcToast.error(msg)
        return { valid: false, model: {} }
      }
    },
    filterReport() {
      const { valid, model } = this.generateFilter
      if (!valid) return null
      const filterBody = {
        ...model,
      }
      return filterBody
    },
    apiClient() {
      return {
        get: async (filter) => {
          const model = this.filterReport
          if (!model) return {}
          const self = this
          self.isBusy = true
          const res = await this.queryCache.fetch(
            GET_REPORT_HISTORY_FLEET,
            {
              filter: {
                ...model,
                column: this.columns.filter(
                  (item) => item != 'startAddress' && item != 'endAddress'
                ),
                ...filter,
              },
            },
            self.reportKey
          )
          self.isBusy = false
          res.reportVehicleTrip.reportVehicleTrips.data = self.transformItemData([
            ...res.reportVehicleTrip.reportVehicleTrips.data,
          ])
          const result = res.reportVehicleTrip.reportVehicleTrips
          self.totalRows = result?.pagination?.total ?? 0
          self.summary = result?.summary ?? {}
          self.message = res.reportVehicleTrip.message
          self.isError = !res.reportVehicleTrip.isSuccessful
          self.modalVerifReport += 1
          self.actualTotal = result?.pagination?.actualTotal ?? 0
          self.isHaveFuelUltrasonicSensor = result?.isHaveFuelUltrasonicSensor ?? false
          return result
        },
      }
    },
    periodeLabel() {
      return this.filters.periode
        ? this.momentDateShortMonth(this.filters.periode[0]) +
            ' - ' +
            this.momentDateShortMonth(this.filters.periode[1])
        : '-'
    },
    minKmMileageLabel() {
      return this.filters?.minKmMileage ? `${this.filters?.minKmMileage} Km` : '-'
    },
    minMinuteTripDurationLabel() {
      return this.filters?.minMinuteTripDuration
        ? `${this.filters?.minMinuteTripDuration} ${this.$t('global.time.minutes')}`
        : '-'
    },
    hasAdditionalFilterScope() {
      return this.hasAccess(['a10f-report-additional-filter:read'])
    },
    licensePlateLabel() {
      if (this.filters.vehicleId && this.filters.vehicleId.length > 0) {
        return this.filterDetail != {} ? this.filterDetail.licensePlate : '-'
      } else {
        return '-'
      }
    },
    vehicleIdCount() {
      return this.filters.vehicleId && this.filters.vehicleId.length > 0
        ? this.filters.vehicleId.length
        : 0
    },
    savedReport() {
      return JSON.parse(localStorage.getItem('saveReports'))
    },
    report() {
      const reports = [...reportList].concat(this.savedReport)
      return reports.find((item) => item.key == this.reportKey)
    },
    filterValue() {
      const storeFilterValue = this.$store.state.report.filterValue

      return this.hasCustomFilters() ? this.transformFields(storeFilterValue) : storeFilterValue
    },
    hasCsvScope() {
      return this.report.downloadOptions?.includes('csv') && this.hasAccess(['csv_report:read'])
    },
    itemDownloadDropdown() {
      let list = []
      if (
        this.getTotalDayReport(this.filters.periode) <= 7 ||
        this.hasOnlyOneVehicleReport(this.filters.vehicleId)
      ) {
        list = list.concat([
          {
            name: 'excel',
            label: this.$t('global.action.downloadExcel'),
            onClick: () => {
              this.downloadReport('xlsx')
            },
            icon: this.excelIcon,
          },
          {
            name: 'pdf',
            label: this.$t('global.action.downloadPdf'),
            onClick: () => {
              this.downloadReport('pdf')
            },
            icon: this.pdfIcon,
          },
        ])
      }
      if (this.hasCsvScope) {
        list.push({
          name: 'csv',
          label: this.$t('global.action.downloadCsv'),
          onClick: () => {
            this.downloadReport('csv')
          },
          icon: this.excelIcon,
        })
      }
      return list
    },
    hasEmail() {
      return this.emails && this.emails.length > 0
    },
  },
  mounted() {
    const emailStorage = localStorage.getItem('emailReceiverReport')
    if (emailStorage) {
      this.recipientsEmail = emailStorage.split(',')
    }

    this.reportKey =
      this.$route.query.key && this.savedReport.some((item) => item.key === this.$route.query.key)
        ? this.$route.query.key
        : this.reportKey
    const { reportFilter, reportFilterValue } = this.getFilterReport(this.reportKey)
    this.$store.commit('report/updateFilter', reportFilter)
    this.$store.commit('report/updateFilterValue', reportFilterValue)
    this.filters = { ...this.$store.state.report.filterReport }
    this.filterDetail = this.getReportDetail(this.reportKey) ?? {}
    this.isMounted = true
    this.track('View ' + this.report.event)
    if (!this.mediaQuery.matches) {
      this.$store.dispatch('layout/toggleMenu', true)
    }
  },
  methods: {
    ishaveOdoFuelSensor(sensors) {
      return (
        sensors?.some((item) => item.sensorCode == SENSOR_CODE.FUEL_ODO) &&
        this.hasAccess(['b1x-new-fuel-expectation:read'])
      )
    },
    transformVehicleType(vehicle) {
      const vehicleType = []
      if (vehicle?.vehicleType) {
        if (vehicle.vehicleType.manufacturer) {
          vehicleType.push(vehicle.vehicleType.manufacturer)
        } else {
          vehicleType.push('-')
        }
        if (vehicle.vehicleType.model) {
          vehicleType.push(vehicle.vehicleType.model)
        } else {
          vehicleType.push('-')
        }
        if (vehicle.vehicleType.type) {
          vehicleType.push(vehicle.vehicleType.type)
        } else {
          vehicleType.push('-')
        }
      }
      return this.huggedText(vehicleType.join(' / '), 40)
    },
    isHaveFuelSensor(sensors, sensorCode, pinCode) {
      if (Array.isArray(sensorCode)) {
        return sensors.some(
          (item) => sensorCode.includes(item.sensorCode) && (!pinCode || item.pinCode == pinCode)
        )
      }
      return sensors.some(
        (item) => item.sensorCode == sensorCode && (!pinCode || item.pinCode == pinCode)
      )
    },
    transformItemData(data) {
      let zeroIndex = []
      let vehicleId = null
      data.forEach((item, index) => {
        if (vehicleId != item.reportVehicleTrip.vehicleId) {
          if (
            zeroIndex.length > 0 &&
            this.isHaveFuelSensor(item.reportVehicleTrip.sensors, SENSOR_CODE.FUEL_ULTRASONIC)
          ) {
            zeroIndex.forEach((zIndex, idx) => {
              data[zIndex].reportVehicleTrip.tripFuel = 'null (<10km)'
            })
          }
          zeroIndex = []
        }
        if (
          item.reportVehicleTrip.tripFuel !== null &&
          item.reportVehicleTrip.tripFuel == 0 &&
          this.isHaveFuelSensor(item.reportVehicleTrip.sensors, SENSOR_CODE.FUEL_ULTRASONIC)
        ) {
          zeroIndex.push(index)
        } else if (
          item.reportVehicleTrip.tripFuel !== null &&
          item.reportVehicleTrip.tripFuel != 0 &&
          this.isHaveFuelSensor(item.reportVehicleTrip.sensors, SENSOR_CODE.FUEL_ULTRASONIC)
        ) {
          if (zeroIndex.length > 0) {
            zeroIndex.forEach((zIndex, index) => {
              data[zIndex].reportVehicleTrip.tripFuel = item.reportVehicleTrip.tripFuel
              if (index == 0) {
                data[zIndex].reportVehicleTrip.isHead = true
                data[zIndex].reportVehicleTrip.numChild = zeroIndex.length + 1
              } else data[zIndex].reportVehicleTrip.isHead = false
            })
            item.reportVehicleTrip.isHead = false
          }
          zeroIndex = []
        }
        vehicleId = item.reportVehicleTrip.vehicleId
        data[index].reportVehicleTrip.sensorType = getFuelSensorString(
          item.reportVehicleTrip.sensors,
          'C'
        )
        if (
          index == data.length - 1 &&
          zeroIndex.length > 0 &&
          this.isHaveFuelSensor(item.reportVehicleTrip.sensors, SENSOR_CODE.FUEL_ULTRASONIC)
        ) {
          zeroIndex.forEach((zIndex, idx) => {
            data[zIndex].reportVehicleTrip.tripFuel = 'null (<10km)'
          })
          zeroIndex = []
        }
      })
      return data
    },
    hasCustomFilters() {
      return (
        this.report?.filter &&
        Object.values(this.report.filter).some(
          (filterConfig) => filterConfig && filterConfig.isCustom === true
        )
      )
    },
    transformFields(formValues) {
      const formValuesKeys = Object.keys(formValues)
      const validKeys = formValuesKeys.filter((key) => formValues[key] !== false)

      return Object.assign(
        {},
        ...validKeys.map((key) => {
          // Get field configuration from reportFilterFields using the custom filter key
          const currentField = this.reportFilterFields[key]
          const value = formValues[key]

          if (currentField && currentField._type === 'number') {
            const isValid = Boolean(value) || value === '0' || value === 0
            return { [key]: isValid ? Number(value) : null }
          } else if (currentField && currentField.type === 'text') {
            if (!formValues[key]) return { [key]: '' }
            return { [key]: formValues[key].trim().replace(/  +/g, ' ') }
          }

          return { [key]: value }
        })
      )
    },
    getBaseFilter(filters) {
      if (!filters || Object.keys(filters).length <= 0) return {}
      const result = {}
      Object.keys(filters).forEach((item) => {
        if (
          filters[item] !== null &&
          ((Array.isArray(filters[item]) && filters[item].length > 0) ||
            (!Array.isArray(filters[item]) && filters[item]))
        ) {
          result[item] = filters[item]
        }
      })
      const { periode, ...otherFilter } = result

      // Transform numeric fields based on custom filters configuration
      const transformedFilter = this.hasCustomFilters()
        ? this.transformFields(otherFilter)
        : otherFilter

      const startDate =
        periode.length > 0 ? moment(periode[0] + ' 00:00:00.000').toISOString() : null
      const endDate = periode.length > 0 ? moment(periode[1] + ' 23:59:59.000').toISOString() : null
      const model = {
        startDate,
        endDate,
        ...transformedFilter,
        disabledOdoFuel: this.hasAccess(['b1x-new-fuel-expectation:read']),
      }
      if (this.hasAccess(['assisted_motion_status:read'])) {
        model.motionStatus = ['assisted', 'moving']
      }
      return model
    },
    handleColumnReport(fields) {
      this.reportFields = fields
      this.tableKey += 1
      this.summaryKey += 1
    },
    forceRerender() {
      this.filters = { ...this.$store.state.report.filterReport }
      this.filterDetail = this.getReportDetail(this.reportKey) ?? {}
      this.tableKey += 1
      this.summaryKey += 1
    },
    showModal() {
      this.$refs['modal-filter'].SetFormValues(this.filterValue)
      this.$bvModal.show(this.report.modal)
    },
    redirectToMap(lat, long) {
      let url = `https://www.google.com/maps/search/?api=1&query=${lat},${long}`
      window.open(url, '_blank')
    },
    handleEmails(emails) {
      this.emails = emails
      this.recipientsEmail = emails
      this.isSubmitEmail = true
      if (emails.length > 0) {
        localStorage.setItem('emailReceiverReport', emails.join())
      }
      this.$bvModal.hide(this.idEmailModal)
      this.$mcToast.info(this.$t('report.text.popupEmailInfo'))
      this.downloadReport(this.reportAfterEmail)
    },
    showDownloader(periode, extension, result) {
      const downloadProgress = {
        status: 'I',
        url: null,
        name:
          `${this.title} ` +
          moment(periode[0]).format('DD MMM YYYY') +
          ' - ' +
          moment(periode[1]).format('DD MMM YYYY') +
          `.${extension}`,
        periode:
          moment(periode[0]).format('DD MMM YYYY') +
          ' - ' +
          moment(periode[1]).format('DD MMM YYYY'),
        downloadedAt: moment().format('DD MMM YYYY HH:mm:ss'),
        type: extension,
        id: result.vehicleTripReport.id,
      }
      if (result.isSuccessful) {
        this.$store.commit('report/doneProgress', downloadProgress)
      } else {
        downloadProgress.status = 'F'
        this.$store.commit('report/failProgress', downloadProgress)
      }
      this.$store.commit('report/showDownloadPopup', true)
    },
    downloadReport(extension, contact) {
      if (
        !this.isSubmitEmail &&
        this.actualTotal > this.totalRows &&
        (!contact || contact.length <= 0)
      ) {
        this.$bvModal.show(this.idEmailModal)
        this.reportAfterEmail = extension
        return
      }
      this.isSubmitEmail = false

      const fileType = this.trackDownloadReport(this.report, extension, contact)

      const model = this.filterReport
      if (!model) {
        setTimeout(() => {
          this.showModal()
        }, 1000)
        return false
      }
      const { periode } = this.filters
      let filterVar = {
        ...model,
        subtitle: `Periode : ${this.momentDateLongMonth(periode[0])} - ${this.momentDateLongMonth(
          periode[1]
        )}`,
        downloadAs: fileType,
        emails: this.emails,
      }
      if (this.report.labelName) {
        filterVar.title = this.report.labelName
      }

      if (contact) {
        filterVar = {
          ...filterVar,
          isShared: true,
          contact: contact,
        }
      }

      this.$apollo
        .query({
          query: EXPORT_REPORT_VEHICLE_TRIP,
          variables: {
            filter: filterVar,
          },
          fetchPolicy: 'no-cache',
        })
        .then((res) => {
          let isSuccessful = res.data.vehicleTripReport.isSuccessful
          if (!this.hasEmail && !contact) {
            this.showDownloader(periode, extension, res.data.vehicleTripReport)
          } else if (contact) {
            if (isSuccessful) this.$mcToast.success(this.$t('report.text.sharedSuccess'))
            else this.$mcToast.error(this.$t('report.text.sharedFailed'))
          }
        })
    },
  },
}
</script>
<style>
#vehicle {
  position: sticky;
  left: 0;
  z-index: 10;
}
</style>
